import { combineReducers, Reducer } from 'redux';

import companySettingsReducer, {
  CompanySettingsState,
  getInitialCompanySettingsState,
} from './modules/companySettings/reducer';
import { ProfileState } from './modules/reports/shared/distributers/actionDistributor';
import segmentsReducer, {
  getInitialSegmentsState,
  SegmentsStateI,
} from './modules/surveys/reducers/segmentsReducer';
import surveysCyclesReducer, { SurveysCyclesState } from './modules/surveys/reducers/surveysCyclesReducer';
import { ThunkFailAction, ThunkSuccessAction } from './services/apiMiddleware';
import spiderGraphReducer, { SpiderGraphState, getInitialSpiderGraphState } from '~src/store/spidergraph/spidergraph.reducer';
import { LOGGED_OUT } from './store/session/actionTypes';
import sessionReducer, { getInitialSessionState, SessionState } from './store/session/session.reducer';
import attributesReducer, {
  AttributesState,
  getInitialAttributesState,
} from './modules/attributes/reducers/attributes.reducer';
import questionsReducer, {
  getInitialQuestionsState,
  QuestionsState,
} from './modules/questions/reducers/questionsReducer';
import cyclesReducer, { CycleState, getInitialCyclesState } from './shared/sidebar/reducers/cycles.reducer';
import surveysReducer, {
  getInitialSurveysState,
  SurveysState,
} from './modules/surveys/reducers/surveysReducer';
import schedulesReducer, {
  getInitialSchedulesState,
  ScheduleStateI,
} from './modules/surveys/reducers/schedulesReducer';
import userManagementReducer, {
  getInitialUserManagementState,
  UserManagementState,
} from './modules/users/reducers/userManagementReducer';
import topicsReducer, { getInitialTopicsState, TopicsState } from './modules/topics/reducers/topicsReducer';
import scalesReducer, { getInitialScalesState, ScalesState } from './modules/scales/reducers/scales.reducer';
import headerReducer, {
  getInitialHeaderState,
  HeaderState,
} from './modules/reports/reportsHeader/reducers/headerReducer';
import userProfileReducer, {
  getInitialUserProfileState,
  UserProfileState,
} from './modules/reports/userProfile/reducers/userProfileReducer';
import companyProfileReducer, {
  getInitialCompanyProfileState,
} from './modules/reports/companyProfile/reducers/companyProfileReducer';
import groupProfileReducer, {
  getInitialGroupProfileState,
  GroupProfileState,
} from './modules/reports/groupProfile/reducers/groupProfileReducer';
import surveyProfileReducer, {
  getInitialSurveyProfileState,
  SurveyProfileState,
} from './modules/reports/surveyProfile/reducers/surveyProfile.reducer';
import heatmapReducer, { HeatmapState, getInitialCompareState } from '~src/store/heatmap/heatmapReducer';
import appReducer, { AppState, getInitialAppState } from './store/app/app.reducer';
import exportReducer, { ExportState, getInitialExportState } from './store/exports/exports.reducer';
import keyResultReducer, {
  getInitialKeyResultsState,
  KeyResultSessionState,
} from './store/okrs/keyResults/keyResult.reducer';
import objectiveReducer, {
  getInitialOkrsSessionState,
  OkrsSessionState,
} from './store/okrs/objective.reducer';
import labelReducer, {
  getInitialLabelSessionState,
  LabelSessionState,
} from './store/okrs/labels/label.reducer';

interface UsersState {
  session: SessionState;
  questions: QuestionsState;
  cycles: CycleState;
}

export interface StoreI {
  app: AppState;
  attributes: AttributesState;
  scales: ScalesState;
  topics: TopicsState;
  user: UsersState;
  surveyManagement: {
    cycles: SurveysCyclesState;
    schedules: ScheduleStateI;
    segments: SegmentsStateI;
    surveys: SurveysState;
  };
  userManagement: UserManagementState;
  reportsManagement: {
    header: HeaderState;
    userProfile: UserProfileState;
    companyProfile: ProfileState;
    groupProfile: GroupProfileState;
    surveyProfile: SurveyProfileState;
  };
  exports: ExportState;
  companySettings: CompanySettingsState;
  heatmap: HeatmapState;
  spidergraph: SpiderGraphState;
  okrs: {
    objectives: OkrsSessionState;
    keyResults: KeyResultSessionState;
    labels: LabelSessionState;
  };
}

function getInitialState(): StoreI {
  return {
    app: getInitialAppState(),
    attributes: getInitialAttributesState(),
    scales: getInitialScalesState(),
    topics: getInitialTopicsState(),
    user: {
      session: getInitialSessionState(),
      questions: getInitialQuestionsState(),
      cycles: getInitialCyclesState(),
    },
    surveyManagement: {
      cycles: getInitialCyclesState(),
      schedules: getInitialSchedulesState(),
      segments: getInitialSegmentsState(),
      surveys: getInitialSurveysState(),
    },
    userManagement: getInitialUserManagementState(),
    reportsManagement: {
      header: getInitialHeaderState(),
      userProfile: getInitialUserProfileState(),
      companyProfile: getInitialCompanyProfileState(),
      groupProfile: getInitialGroupProfileState(),
      surveyProfile: getInitialSurveyProfileState(),
    },
    exports: getInitialExportState(),
    companySettings: getInitialCompanySettingsState(),
    heatmap: getInitialCompareState(),
    spidergraph: getInitialSpiderGraphState(),
    okrs: {
      objectives: getInitialOkrsSessionState(),
      keyResults: getInitialKeyResultsState(),
      labels: getInitialLabelSessionState(),
    },
  };
}

const rootReducer: Reducer<StoreI, ThunkSuccessAction<unknown> | ThunkFailAction<unknown>> = (store, action) => {
  if (action.type === LOGGED_OUT) {
    return getInitialState();
  }

  return combineReducers({
    app: appReducer,
    attributes: attributesReducer,
    scales: scalesReducer,
    topics: topicsReducer,
    user: combineReducers({
      session: sessionReducer,
      questions: questionsReducer,
      cycles: cyclesReducer,
    }),
    surveyManagement: combineReducers({
      cycles: surveysCyclesReducer,
      schedules: schedulesReducer,
      segments: segmentsReducer,
      surveys: surveysReducer,
    }),
    userManagement: userManagementReducer,
    reportsManagement: combineReducers({
      header: headerReducer,
      userProfile: userProfileReducer,
      companyProfile: companyProfileReducer,
      groupProfile: groupProfileReducer,
      surveyProfile: surveyProfileReducer,
    }),
    exports: exportReducer,
    companySettings: companySettingsReducer,
    heatmap: heatmapReducer,
    spidergraph: spiderGraphReducer,
    okrs: combineReducers({
      objectives: objectiveReducer,
      keyResults: keyResultReducer,
      labels: labelReducer,
    }),
  })(store, action);
};

export default rootReducer;
